import { useEffect } from 'react';
import { useInstitutionsStore } from '@/stores/institutions';

/**
 * Custom hook to set a favicon dynamically based on the current institution or a provided URL.
 * Automatically removes existing favicon links before applying the new one.
 * Skips applying if the browser is Safari or no favicon is available.
 * @param favicon - Optional URL of the favicon to use. Defaults to '/favicon.svg' if not provided.
 */
export const useCustomFavicon = (favicon?: string) => {
  const { currentInstitution } = useInstitutionsStore((state) => ({
    currentInstitution: state.currentInstitution,
  }));

  const applyFavicon = (faviconLink?: string) => {
    const existingLinks = document.querySelectorAll("link[rel~='icon']");
    existingLinks.forEach((link) => link.parentNode?.removeChild(link));

    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = `${faviconLink ?? '/favicon.svg'}?v=${Date.now()}`;
    document.head.appendChild(link);
  };

  useEffect(() => {
    const isSafari = /^((?!chrome|chromium|android).)*safari/i.test(
      navigator.userAgent,
    );

    if (isSafari || (!currentInstitution?.whitelabel?.enabled && !favicon))
      return;
    applyFavicon(currentInstitution?.whitelabel?.faviconImageUrl ?? favicon);
  }, [
    currentInstitution?.whitelabel?.enabled,
    currentInstitution?.whitelabel?.faviconImageUrl,
    favicon,
  ]);
};
